<!--
  BasePage — the tabbed shell host for the authenticated main app.

  Why this is a thin wrapper around <tabbar-navigation> with a CONTENT-LESS
  <f7-page> (`:page-content="false"`, playbook Section 5 rows 17 + 25):

  In our routes config, the parent route `/` has `tabs: [...]` plus a Vue
  component (`component: BasePage`). Per Framework7 docs, the parent's
  component must render the F7 tab CONTAINERS (`<f7-tabs routable>` with
  `<f7-tab id="...">` children) — NOT a full `<f7-page>` (with its own
  `.page-content`). Each tab's loaded component (HomePage, WalletPage, ...)
  provides its OWN `<f7-page>` + `<f7-navbar>`, which F7 mounts INSIDE the
  matching `<f7-tab>` element.

  Confirmed from F7 v9 source:
    - framework7/modules/router/tab.js (tabLoad → tabComponentLoader)
      mounts the resolved component INTO the `<f7-tab id="...">` element.
    - framework7-vue/components/tab.js adds class="tab" to the host.
    - The loaded HomePage's `<f7-page>` becomes the actual page; the
      `<f7-tab>` wrapper provides positioning/visibility via tab-active.

  A NORMAL `<f7-page>` here (default `page-content: true`) would create a
  NESTED `.page-content` layout (outer page-content = BasePage, inner
  page-content = HomePage). F7's CSS for routable tabs doesn't account for
  that — verified empirically: the outer tabbar collapses to 0×0 and the
  inner active tab is sized to ~50px, hiding all body content (only the
  inner navbar shows).

  `<f7-page :page-content="false">` avoids that: per
  `framework7-vue/components/page.js`, `page-content: false` takes the OTHER
  render branch — NO `f7PageContent` wrapper, direct children only (toolbar +
  routable tabs), so there is still only ONE real `.page-content` in the whole
  tree (the loaded tab's own). It DOES add the base `.page` class F7 core
  needs — `.page > .tabs { height: 100% }` (framework7-bundle.css) is F7's
  own CSS for exactly this "routable-tabs parent page" shape, so `<f7-tabs>`
  still fills the full height and the toolbar stays pinned to the bottom,
  unchanged visually from the row-17-era plain-fragment version.

  Row 25 (`master:true` + a pushed `hideTabbar` detail route + back-navigation
  — e.g. `/orders/box/:id/totals/`): the PLAIN-FRAGMENT version (no wrapping
  element, hence no `.page` class at all) broke `router.back()` — F7 core's
  `back.js` / `navigate.js` locate the previous/current page purely via
  `.page`/`.page-current`/`.page-previous` CLASS selectors on `view.$el`'s
  direct children. Without the base `.page` class, this shell was invisible to
  that bookkeeping: `navigate.js`'s forward-nav old-page detection never
  reclassed it `page-previous` when the detail route was pushed, so
  `back.js`'s `.page-current → .prevAll('.page-previous')` lookup came up empty
  on the way back, and `back()` fell through to re-resolving `/` from scratch —
  re-mounting BasePage as a SECOND, duplicate shell (see playbook Section 5 row 25 for
  the full trace, reproduced on the supplier-side sibling route). The `.page`
  class fixes this: F7 now correctly reclasses the shell `page-previous` on the
  way out and finds/reuses it on the way back.

  The tabbar visibility (`v-show="showTabbar"`) is owned by
  TabbarNavigation, which injects `showTabbar` from AppLayout.
-->
<template>
  <f7-page :page-content="false" name="shell">
    <tabbar-navigation></tabbar-navigation>
  </f7-page>
</template>

<script setup>
import TabbarNavigation from '@/components/TabbarNavigation.vue';

defineOptions({
  name: 'BasePage',
});
</script>
